/**
 * Fix B — keep the main thread responsive during the cold multi-pane load.
 *
 * The ~1.25s freeze is cornerstone's volume build (createAndCacheVolume +
 * setVolumesForViewports/GPU texture upload), which app code can't chunk. The
 * aggravating factor we *can* control: the default 4-up view builds all four
 * panes' volumes in the same synchronous burst, stacking those uploads into one
 * unbroken block so wheel/scroll input is dropped for seconds.
 *
 * `createSerialQueue` runs attach tasks one at a time and yields to the event
 * loop before each, so the per-pane GPU uploads are spread across frames and the
 * browser can service scroll/paint in the gaps. A rejected/aborted task never
 * wedges the queue (the chain advances on settle, not just success).
 */
export const yieldToEventLoop = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0));

export function createSerialQueue(
  yieldFn: () => Promise<void> = yieldToEventLoop,
): <T>(task: () => Promise<T>) => Promise<T> {
  let tail: Promise<unknown> = Promise.resolve();

  return function run<T>(task: () => Promise<T>): Promise<T> {
    const result = tail.then(async () => {
      await yieldFn();
      return task();
    });
    // Advance the chain on settle (success OR failure) so one bad attach can't
    // wedge every later pane.
    tail = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  };
}

/**
 * Process-wide queue shared by every VolumeViewport so panes across the grid
 * serialize their attaches against one another (a per-component queue wouldn't).
 */
export const volumeAttachQueue = createSerialQueue();
